/* eslint class-methods-use-this: 0, dot-notation: 0 */
import { get, includes, mixin } from 'lodash';
import Vue from 'vue';

import {
  ItemTableConsts,
  ItemTypeConsts,
  MdmVendorStates,
  SystemInsightsConsts,
} from 'client/util/Constants';
import CollectionModule from 'client/store/modules/CollectionModule';
import SudoMixin, { addSudoFeaturesToState, processSudoBoundItems, resetSudoState } from 'client/store/mixins/SudoMixin';
import SystemApi from 'client/api/v2/SystemApi';
import SystemFilters from 'components/ItemTable/CollectionFilterComponents/constants/SystemFilters';
import SystemInsightsApi from 'client/api/v2/SystemInsightsApi';
import SystemMixin from 'client/store/mixins/SystemMixin';
import SystemUserApi from 'client/api/v2/SystemUserApi';

const { parentOSType } = ItemTableConsts;
const { darwin, windows } = parentOSType;
const { systemState } = SystemInsightsConsts;
const { internal } = MdmVendorStates;

const BOUND_ITEMS_PAGE_SIZE = 25;

/**
 * @class The SystemCollectionClass extends the CollectionModule and contains
 * actions and getters for systems
 */
export class SystemCollectionClass extends CollectionModule {
  constructor(collectionType = '', ...args) {
    super(...args);
    this.acknowledged = collectionType === ItemTypeConsts.systemUnacknowledged ? false : '';
    this.needsAttention = collectionType === ItemTypeConsts.systemNeedsAttention;
  }

  extractModule() {
    const module = super.extractModule();

    mixin(module.actions, SudoMixin.actions);
    mixin(module.getters, SudoMixin.getters);
    mixin(module.mutations, SudoMixin.mutations);

    module.actions.acknowledgeSystem = (...params) => this.acknowledgeSystem(...params);
    module.actions.acknowledgeSelectedSystems = (...params) => this
      .acknowledgeSelectedSystems(...params);
    module.actions.fetchInstalledBy = (...params) => this.fetchInstalledBy(...params);
    module.actions.fetchNoFilterTotalCount = (...params) => this.fetchNoFilterTotalCount(...params);
    module.actions.fetchTotalCount = (...params) => this.fetchTotalCount(...params);
    module.actions.fetchTotalInternalMdmDeviceCount = (...params) => this
      .fetchTotalInternalMdmDeviceCount(...params);
    module.actions.hasASystemOfOs = (...params) => this.hasASystemOfOs(...params);
    module.actions.fetchSystemInsightsData = (...params) => this.fetchSystemInsightsData(...params);
    module.actions.resetDevicePanelBoundItemsPagination = (...params) => this
      .resetDevicePanelBoundItemsPagination(...params);
    module.actions.setUsePaginationMethodForBoundDevices = (...params) => this
      .setUsePaginationMethodForBoundDevices(...params);

    module.getters.getAppliedFilters = (...params) => this.getAppliedFilters(...params);
    module.getters.getFdeType = (...params) => this.getFdeType(...params);
    module.getters.getSystemNIC = (...params) => this.getSystemNIC(...params);
    module.getters.getSystemsIneligibleForMFADisablement = (...params) => this
      .getSystemsIneligibleForMFADisablement(...params);
    module.getters.getSystemsIneligibleForMFAEnablement = (...params) => this
      .getSystemsIneligibleForMFAEnablement(...params);
    module.getters.getSystemsIneligibleForSystemInsightEnablement = (...params) => this
      .getSystemsIneligibleForSystemInsightEnablement(...params);
    module.getters.getSystemsIneligibleForSystemInsightDisablement = (...params) => this
      .getSystemsIneligibleForSystemInsightDisablement(...params);
    module.getters.getSystemsUnacknowledged = (...params) => this
      .getSystemsUnacknowledged(...params);
    module.getters.hasBadStateDevices = (...params) => this.hasBadStateDevices(...params);
    module.getters.hasUnacknowledgedSystems = (...params) => this
      .hasUnacknowledgedSystems(...params);
    module.getters.isADJoinedDeviceSelected = (...params) => this
      .isADJoinedDeviceSelected(...params);
    module.getters.isAzureADJoinedDeviceSelected = (...params) => this
      .isAzureADJoinedDeviceSelected(...params);
    module.getters.isFdeKeyPresent = (...params) => this.isFdeKeyPresent(...params);

    module.mutations.RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED = (...params) => this
      .RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED(...params);
    module.mutations.RESET_SELECTED_ITEMS = (...params) => this.RESET_SELECTED_ITEMS(...params);
    module.mutations.SET_SKIP = (...params) => this.SET_SKIP(...params);
    module.mutations.SET_TOTAL_COUNTS = (...params) => this.SET_TOTAL_COUNTS(...params);
    module.mutations.SET_USE_PAGINATION_METHOD_FOR_BOUND_DEVICES = (...params) => this
      .SET_USE_PAGINATION_METHOD_FOR_BOUND_DEVICES(...params);

    return module;
  }

  // --------------------------------------------------------------------------
  // State
  // --------------------------------------------------------------------------

  getInitialState() {
    const state = addSudoFeaturesToState(super.getInitialState());
    const acknowledgedValue = typeof this.acknowledged === 'boolean'
      ? this.acknowledged.toString() : '';

    state.features.items.filters = [
      {
        name: 'acknowledged',
        operator: 'eq',
        value: acknowledgedValue,
      },
    ];

    if (this.needsAttention) {
      state.features.items.filters = SystemFilters.secureTokenRemediationFilters();
      state.features.items.searchTerm = `OS Family: ${darwin}`;
    }

    state.features.boundItemsPagination = {
      currentSkip: 0,
    };

    state.features.usePaginationMethodForBoundDevices = false;

    return state;
  }

  // --------------------------------------------------------------------------
  // Actions
  // --------------------------------------------------------------------------

  /**
   * After bound items are retrieved iterate through each one to set the sudoer
   * status of each bound item.
   * @todo Handle other compiled attributes such as LDAP Bind DN, Samba Service
   * Account, as other UserItemLists are replaced by the ItemTable.
   */
  processBoundItems(context = {}, boundItems = {}) {
    super.processBoundItems(context, boundItems);
    processSudoBoundItems(context, boundItems);
  }

  async fetchCollection(context, { next = false } = {}) {
    const {
      showBoundItems,
      usePaginationMethodForBoundDevices,
    } = context.state.features;

    /**
     * Zach Smith
     * Bug ticket: https://jumpcloud.atlassian.net/browse/HY-2260
     * Spike ticket: https://jumpcloud.atlassian.net/browse/HY-2261
     *
     * For some clients with large JSON attributes on User Groups that their Devices are bound to,
     * the legacy BoundItemsApi (limit=1000) call was erroring in the RPC. Those custom attributes
     * were being returned on EVERY item in that call (multiple times on every item) making the
     * response size over the 4MB limit that RPCs have. So, we had to create a solution
     * that did not use the BoundItemsApi in that capacity.
     *
     * We are still using the BoundItemsApi - however, we are using a different endpoint that does
     * not return as much data in each result set.
     *
     * In the legacy call `BoundItemsApi.initialize` (limit=1000) we were getting back the item's
     * entire data set, PLUS the attributes and traversal data on each item as well.
     *
     * For the new call `CollectionApi.checkForBoundItems` we are only getting back the attributes
     * and traversal data for each item bound, plus its ID and item type. This decreases the
     * response size significantly and allows for speedy pagination.
     *
     * TL;DR - The best short-term solution ended up being using the generic API fetch to get back
     * a page of devices, and then using the BoundItemsApi to check if any of those devices are
     * bound to the current user.
     */
    if (usePaginationMethodForBoundDevices && showBoundItems) {
      const paginateBoundDevicesResult = await this.paginateBoundDevices(context);

      return paginateBoundDevicesResult;
    }

    return super
      .fetchCollection(context, { next })
      .then(() => {
        let payload;
        const filters = context.getters.getFilters;
        if (filters && filters.find(obj => obj.name === 'active')) {
          let collection = context.getters.getCollection;
          collection = collection.filter(sys => sys.os !== 'iOS');
          payload = {
            results: collection,
            totalCount: context.getters.getTotalCount,
            nextPageToken: context.getters.getNextPageToken,
            skipToken: context.getters.getSkipToken,
          };
          if (context.state.entities.items.totalCountOfAllItems === null) {
            payload.totalCountOfAllItems = payload.totalCount;
          }
          context.commit('SET_COLLECTION', payload);
        }
      });
  }

  fetchInstalledBy(context, item) {
    const systemuserId = get(item, 'provisionMetadata.provisioner.provisionerId', '');
    return SystemUserApi.get(systemuserId).then(systemUser => ({
      systemuserId,
      username: systemUser.username,
    }));
  }

  fetchTotalCount(context) {
    const { state } = context;
    return this.Api.fetchTotalCount(state.features.items.filters);
  }

  fetchTotalInternalMdmDeviceCount() {
    const filters = [SystemFilters.mdm(internal)];
    return this.Api.fetchTotalCount(filters);
  }

  fetchNoFilterTotalCount() {
    return this.Api.fetchTotalCount();
  }

  acknowledgeSystem(context, { id }) {
    return this.updateItem(context, { id, attributes: { acknowledged: true } }).then(() => {
      context.commit('DELETE_ITEMS', [id]);
    });
  }

  acknowledgeSelectedSystems(context) {
    const { state, getters } = context;
    const selectedUnacknowledgedSystems = this.getSystemsUnacknowledged(state, getters);
    const acknowledgeSystemPromises = selectedUnacknowledgedSystems.map(system => (
      this.acknowledgeSystem(context, { id: system.id })
    ));

    return Promise.all(acknowledgeSystemPromises);
  }

  hasASystemOfOs(context, os) {
    return SystemApi.fetch(0, 1, '', null, null, [SystemFilters.os({ os })])
      .then(data => data.totalCount > 0);
  }

  async fetchSystemInsightsData(context, { id, endpoint }) {
    const response = await SystemInsightsApi.getSystemInsight(id, endpoint);
    return response.data[0];
  }

  resetDevicePanelBoundItemsPagination(context) {
    context.commit('SET_USE_PAGINATION_METHOD_FOR_BOUND_DEVICES', false);
    context.commit('SET_SKIP', 0);
    context.commit('RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED');
  }

  setSearchTerm(context, payload) {
    const {
      boundItemsPagination,
      showBoundItems,
      usePaginationMethodForBoundDevices,
    } = context.state.features;

    if (usePaginationMethodForBoundDevices) {
      // ensure selected items/counts don't carry over to the new search
      context.commit('RESET_SELECTED_ITEMS');

      if (showBoundItems && boundItemsPagination.currentSkip !== 0) {
        context.commit('SET_SKIP', 0);
      }
    }

    super.setSearchTerm(context, { ...payload, preventReset: false });
  }

  setUsePaginationMethodForBoundDevices(context, value) {
    context.commit('SET_USE_PAGINATION_METHOD_FOR_BOUND_DEVICES', value);
  }

  showAllItems(context) {
    if (context.state.features.usePaginationMethodForBoundDevices) {
      context.commit('RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED');
      context.commit('SET_TOTAL_COUNTS', { totalCountOfAllItems: null });
    }

    super.showAllItems(context);
  }

  showBoundItems(context) {
    if (
      context.state.features.usePaginationMethodForBoundDevices
      && context.state.features.boundItemsPagination.currentSkip !== 0
    ) {
      context.commit('SET_SKIP', 0);
      context.commit('RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED');
    }

    super.showBoundItems(context);
  }

  // --------------------------------------------------------------------------
  // Getters
  // --------------------------------------------------------------------------

  // This is for Mac devices only!
  async hasBadStateDevices() {
    const result = await SystemApi.fetch(0, 1, null, `OS Family: ${darwin}`, null,
      SystemFilters.secureTokenRemediationFilters());
    return result.totalCount > 0;
  }

  async hasUnacknowledgedSystems() {
    const { totalCount } = await SystemApi.fetch(0, 1, null, null, null,
      [SystemFilters.acknowledged(false)]);
    return totalCount > 0;
  }

  selectedAdHelper(state = {}, getters = {}, predicate) {
    // Note we do not use getSelectedSystems here, because bound items may not have
    // come in yet, but systems has what we need to check for domain membership
    // - avoid an undefined `this` binding in SystemMixin :/ beats duplicating the logic
    const pred = predicate.bind(SystemMixin);
    return getters.getSelectedItems
      .map(id => (state.entities && state.entities.items.byId[id]) || undefined)
      .filter(item => item !== undefined)
      .some(pred);
  }

  isADJoinedDeviceSelected(state = {}, getters = {}) {
    return this.selectedAdHelper(state, getters, SystemMixin.isWindowsDeviceDomainJoined);
  }

  isAzureADJoinedDeviceSelected(state = {}, getters = {}) {
    return this.selectedAdHelper(state, getters, SystemMixin.isAzureDomainJoined);
  }

  isFdeKeyPresent() {
    return item => SystemMixin.isFdeKeyPresent(item);
  }

  getAppliedFilters(state = {}) {
    const { filters } = state.features.items;

    return filters.filter(filter => (
      filter.name === 'active'
      || filter.name === 'os'
      || filter.name === 'hasServiceAccount'
      || filter.name === 'serviceAccountState.passwordAPFSValid'
      || filter.name === 'serviceAccountState.passwordODValid'
      || filter.name === 'serviceAccountState.hasSecureToken'
      || filter.name === 'osFamily'
      || filter.name === 'fde.active'
      || filter.name === 'mdm.vendor'
      || filter.name === 'osVersionDetail.patch'
      || filter.name === 'version'
      || filter.name === 'osVersionDetail.distributionName'
      || filter.name === 'lastContact'
      || filter.name === 'osVersionDetail.releaseName'
      || filter.name === 'osVersionDetail.revision'
      || filter.name === 'osVersionDetail.osName'
      || filter.name === 'osVersionDetail.major'
      || filter.name === 'osVersionDetail.minor'
      || filter.name === 'osVersionDetail.patch'
      // filter must have a value, type, and displayValue to show in the UI
    ) && (filter.value !== '' && filter.type && filter.displayValue));
  }

  getSystemNIC() {
    return item => SystemMixin.getSystemNIC(item);
  }

  // getFdeType() is checking to see if any of the ids passed are FDE enabled (fde.keyPresent)
  // then adding the system types corresponding FDE policy name to an object which is then
  // returned as an array of its keys.
  /* eslint-disable */
  getFdeType(state = {}) {
    return (ids) => {
      const { items } = state.entities || {};

      const types = ids.reduce((acc, id) => {
        if (items.byId[id] && items.byId[id].fde && items.byId[id].fde.keyPresent) {
          if (SystemMixin.isMacOS(items.byId[id])) {
            acc['FileVault'] = true;
          }
          if (items.byId[id].osFamily === windows) {
            acc['Bitlocker'] = true;
          }
        }
        return acc;
      }, {});
      return Object.keys(types);
    }
  }

  getSelectedSystems(state = {}, getters = {}) {
    return getters.getSelectedItems
      .map(id => (state.entities && state.entities.boundItems.byId[id]) || undefined)
      .filter(item => item !== undefined);
  }

  // Returns an array of systems that are not eligible to have System Insights Enabled
  getSystemsIneligibleForSystemInsightEnablement(state = {}, getters = {}) {
    const selectedItems = this.getSelectedSystems(state, getters);

    const alreadyEnabledSystems = selectedItems.filter(item => get(item, 'systemInsights.state') === systemState.enabled);
    const non64BitSystems = selectedItems.filter(item => !includes(item.arch, '64'));

    return {
      alreadyEnabledSystems,
      non64BitSystems,
    };
  }

  // Returns an array of systems that are not eligible to have System Insights Disabled
  getSystemsIneligibleForSystemInsightDisablement(state = {}, getters = {}) {
    const selectedItems = this.getSelectedSystems(state, getters);

    return selectedItems.filter((item) => {
      const state = get(item, 'systemInsights.state');
      return state === systemState.deferred || state === systemState.disabled;
    });
  }

  // Returns an array of systems that are not eligible to have MFA disabled
  getSystemsIneligibleForMFADisablement(state = {}, getters = {}) {
    const selectedItems = this.getSelectedSystems(state, getters);

    return selectedItems.filter(item => !item.allowMultiFactorAuthentication);
  }

  // Returns an array of systems that are not eligible to have MFA enabled
  getSystemsIneligibleForMFAEnablement(state = {}, getters = {}) {
    const selectedItems = this.getSelectedSystems(state, getters);

    /**
     * Ineligible systems include:
     * - Systems that already have MFA enabled
     * - Linux systems with SSH password auth and public key auth
     */
    const alreadyEnabledSystems = selectedItems
      .filter(item => item.allowMultiFactorAuthentication);
    const ineligibleLinuxSystems = selectedItems
      .filter(item => !SystemMixin.isMultifactorSupported(item));

    return {
      alreadyEnabledSystems,
      ineligibleLinuxSystems,
    };
  }

  getSystemsUnacknowledged(state = {}, getters = {}) {
    const selectedItems = this.getSelectedSystems(state, getters);

    // needs to be specifically `false` - cannot merely be falsy
    return selectedItems.filter(item => item.acknowledged === false);
  }

  // --------------------------------------------------------------------------
  // Mutations
  // --------------------------------------------------------------------------

  RESET_BOUND_ITEMS_TOTAL_COUNT_AND_FETCHED(state = {}) {
    Vue.set(state.features, 'hasBoundItemsTotalCountBeenSet', false);
    Vue.set(state.features, 'haveBoundItemsBeenFetched', false);
  }

  RESET_COLLECTION(state = {}, payload = {}) {
    super.RESET_COLLECTION(state, payload);

    if (!payload || (payload && !payload.soft)) {
      const initialState = this.getInitialState();
      resetSudoState(state, initialState);
    }
  }

  SET_SKIP(state = {}, skip) {
    Vue.set(state.features.boundItemsPagination, 'currentSkip', skip);
  }

  SET_TOTAL_COUNTS(state = {}, { totalCount, totalCountOfAllItems, boundItemsTotalCount }) {
    if (totalCount !== undefined) {
      Vue.set(state.entities.items, 'totalCount', totalCount);
    }

    if (totalCountOfAllItems !== undefined) {
      Vue.set(state.entities.items, 'totalCountOfAllItems', totalCountOfAllItems);
    }

    if (boundItemsTotalCount !== undefined) {
      Vue.set(state.entities.boundItems, 'totalCount', boundItemsTotalCount);
    }
  }

  SET_USE_PAGINATION_METHOD_FOR_BOUND_DEVICES(state = {}, value) {
    Vue.set(state.features, 'usePaginationMethodForBoundDevices', value);
  }

  RESET_SELECTED_ITEMS(state = {}) {
    Vue.set(state.features, 'selectedItems', { ids: [] });
  }

  // --------------------------------------------------------------------------
  // Helper functions
  // --------------------------------------------------------------------------

  getPaginationAndCountRequests(context) {
    const { currentSkip } = context.state.features.boundItemsPagination;
    const { boundItemsUrl, hasBoundItemsTotalCountBeenSet } = context.state.features;
    const { searchTerm, sort } = context.state.features.items;

    // pagination request
    const requests = [
      this.Api.fetch(currentSkip, BOUND_ITEMS_PAGE_SIZE, sort, searchTerm),
    ];

    // bound items count request, if bound items count hasn't been set yet
    if (!hasBoundItemsTotalCountBeenSet) {
      requests.push(this.Api.getBoundItemCount(boundItemsUrl));
    }

    return requests;
  }

  async getBoundItemsFromResults(boundItemsUrl, results) {
    const resultIds = results.map(result => result.id);
    const { boundItems, boundItemIds } = await this.Api.checkForBoundItems(boundItemsUrl, resultIds);

    return { boundItems, boundItemIds };
  }

  getResultsFilteredByBoundStatus(boundItemIds, results) {
    const boundItemIdsSet = new Set(boundItemIds);
    const resultsFilteredByBoundStatus = results.reduce((acc, result) => {
      if (boundItemIdsSet.has(result.id)) {
        acc.push({ ...result, _id: result.id });
      }

      return acc;
    }, []);

    return resultsFilteredByBoundStatus;
  }

  /**
   * @param {*} context Vuex context object
   * @returns empty array - does not matter
   *
   * This method attempts at a new pattern of grabbing bound devices for any
   * given association to them. For this specific use case, it was a User -> Devices
   * association.
   * 
   * In short, we've split the legacy `BoundItemsApi.initialize` call into two parts,
   * in order to shrink the RPC response size down to below 4MB.
   * 
   * `1st call` use the generic API fetch function to grab a page of devices, based on
   * your skip value, sort order, and search term.
   * 
   * `2nd call` use the device IDs from the first call, and call the BoundItemsApi with those IDs
   * to determine if any of the devices are bound to this User.
   * 
   * If any are, we add them to state and append them to the ItemTable.
   */
  async paginateBoundDevices(context) {
    const requests = this.getPaginationAndCountRequests(context);

    // Wait for the Promises to resolve
    const [{ results }, boundItemsCount] = await Promise.all(requests);

    if (boundItemsCount != null) {
      context.commit('SET_TOTAL_COUNTS', {
        boundItemsTotalCount: boundItemsCount,
        totalCount: boundItemsCount,
        totalCountOfAllItems: boundItemsCount,
      });

      context.commit('BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET');
    }

    // We don't want to keep executing this method if there are no initial
    // results - exit early here
    if (!results.length) return [];

    // Next use the BoundItemsApi to check if any of the returned items
    // from the first call are bound to this User
    const { boundItems, boundItemIds } = await this.getBoundItemsFromResults(
      context.state.features.boundItemsUrl,
      results,
    );

    // We don't want to keep executing this method if there are no
    // bound items to place in state - exit early here
    if (!boundItems.length) return [];

    const resultsFilteredByBoundStatus = this.getResultsFilteredByBoundStatus(boundItemIds, results);

    // Now, we "mimic" the mutations used by CollectionModule.fetchCollection to
    // insert the correct values into their corresponding state fields to fill in
    // the ItemTable and the counts text beside the search bar.
    context.commit('SET_COLLECTION', {
      results: resultsFilteredByBoundStatus,
      nextPageToken: context.getters.getNextPageToken,
      skipToken: context.getters.getSkipToken,
    });
    context.commit('SELECT_ITEMS', { ids: boundItemIds || [] });
    context.commit('SET_PREVIOUSLY_SELECTED_ITEMS', { ids: boundItemIds || [] });
    context.commit(
      'SET_SKIP',
      context.state.features.boundItemsPagination.currentSkip + BOUND_ITEMS_PAGE_SIZE
    );

    const boundItemsIdAdded = boundItems.map(item => ({ ...item, _id: item.id }));

    context.commit('SET_BOUND_ITEMS', {
      results: boundItemsIdAdded,
      totalCount: context.getters.getBoundItemsTotalCount,
    });

    this.processBoundItems(context, boundItemsIdAdded);

    return boundItemIds;
  }

  /**
   * HY-2260
   * Meant to be a first stab at an alternative to the Bound Items pagination
   * fix above - this method turned out to be slower than the last - so we shelved it.
   */
  // async experimentalAutoPaginate(context) {
  //   const { boundItemsUrl } = context.state.features;
  //   const { searchTerm, sort } = context.state.features.items;
  //   const boundItemsCount = await this.Api.getBoundItemCount(boundItemsUrl);

  //   context.commit('SET_TOTAL_COUNTS', {
  //     boundItemsTotalCount: boundItemsCount,
  //     totalCount: boundItemsCount,
  //     totalCountOfAllItems: boundItemsCount,
  //   });

  //   context.commit('BOUND_ITEMS_TOTAL_COUNT_HAS_BEEN_SET');

  //   const fetchItemsRequestsArray = [];
  //   for (let skip = 0; skip < boundItemsCount; skip += BOUND_ITEMS_PAGE_SIZE) {
  //     fetchItemsRequestsArray.push(
  //       this.Api.fetch(skip, BOUND_ITEMS_PAGE_SIZE, sort, searchTerm),
  //     );
  //   }

  //   const results = await Promise.all(fetchItemsRequestsArray);

  //   const onlyResults = results.reduce((acc, result) => {
  //     acc.push(...result.results);

  //     return acc;
  //   }, []);

  //   for (let i = 0; i < results.length; i += 1) {
  //     const resultIds = results[i].results.map(result => result.id);
  //     this.Api
  //       .checkForBoundItems(boundItemsUrl, resultIds)
  //       .then(({ boundItems, boundItemIds }) => {
  //         const resultsFilteredByBoundStatus = this.getResultsFilteredByBoundStatus(boundItemIds, onlyResults);
  //         const boundItemsIdAdded = boundItems.map(item => ({ ...item, _id: item.id }));

  //         context.commit('SET_COLLECTION', {
  //           results: resultsFilteredByBoundStatus,
  //           nextPageToken: context.getters.getNextPageToken,
  //           skipToken: context.getters.getSkipToken,
  //         });
  //         context.commit('SELECT_ITEMS', { ids: boundItemIds || [] });
  //         context.commit('SET_PREVIOUSLY_SELECTED_ITEMS', { ids: boundItemIds || [] });

  //         context.commit('SET_BOUND_ITEMS', {
  //           results: boundItemsIdAdded,
  //           totalCount: context.getters.getBoundItemsTotalCount,
  //         });

  //         this.processBoundItems(context, boundItemsIdAdded);
  //       });
  //   }

  //   context.commit('BOUND_ITEMS_HAVE_BEEN_FETCHED');
  // }
}

export function createSystemCollection(collectionType) {
  return new SystemCollectionClass(collectionType, SystemApi, '_id', 'displayName').extractModule();
}
